import {
  AppstoreOutlined,
  CheckCircleFilled,
  DownloadOutlined,
  EyeOutlined,
  FileSearchOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { Alert, Button, Drawer } from 'antd';
import React from 'react';

type LineageHelpDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const guideSteps = [
  { icon: <AppstoreOutlined />, label: 'Choose', detail: 'Select a consuming QVF.' },
  { icon: <FileSearchOutlined />, label: 'Inspect', detail: 'Open any source, QVD, App, or analytic.' },
  { icon: <ShareAltOutlined />, label: 'Recenter', detail: 'Use Explore from here.' },
  { icon: <CheckCircleFilled />, label: 'Trace', detail: 'Show all used fields or isolate one.' },
];

export const LineageHelpDrawer: React.FC<LineageHelpDrawerProps> = ({ open, onClose }) => (
  <Drawer
    className="lineage-help-drawer"
    title="Lineage Center Help"
    placement="right"
    width={600}
    open={open}
    onClose={onClose}
    destroyOnClose
    footer={(
      <div className="lineage-help-footer">
        <Button aria-label="Close Lineage Center help" onClick={onClose}>Close</Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          href="/help/lineage-center-user-guide.pdf"
          download="click-ci-lineage-center-user-guide.pdf"
        >
          Download PDF guide
        </Button>
      </div>
    )}
  >
    <div className="lineage-help-content">
      <section className="lineage-help-intro">
        <span>QUICK GUIDE</span>
        <h2>Trace a field in under two minutes</h2>
        <p>Follow Qlik data from its original source, through QVD stages, to a consuming App and analytic.</p>
        <div className="lineage-help-quick-path" aria-label="Lineage Center quick path">
          {guideSteps.map((step, index) => (
            <div key={step.label}>
              <i>{step.icon}</i>
              <span><b>{index + 1}. {step.label}</b><small>{step.detail}</small></span>
            </div>
          ))}
        </div>
      </section>

      <section className="lineage-help-section">
        <header><em>1</em><div><h3>Choose a consuming App</h3><p>Select a QVF from Consumer Apps. The selected App stays dominant in teal while shared consumers remain visible.</p></div></header>
        <img src="/help/lineage/app-overview.png" alt="Lineage Center consuming App overview" />
        <p className="lineage-help-caption">Read left to right: Sources, 00 Extract, 01 Transform, 02 Model, 03 Apps, and Analytics.</p>
      </section>

      <section className="lineage-help-section">
        <header><em>2</em><div><h3>Inspect a node and recenter</h3><p>Select any source, QVD, App, or analytic to see its fields, identifiers, and supporting evidence.</p></div></header>
        <img src="/help/lineage/inspect-recenter.png" alt="Selected lineage node and evidence inspector" />
        <p className="lineage-help-caption">Choose <b>Explore from here</b> to redraw reachable upstream sources and downstream consumers around that node.</p>
      </section>

      <section className="lineage-help-section">
        <header><em>3</em><div><h3>Trace verified field paths</h3><p>All fields shows the union of verified paths. Select a field chip to isolate one server-projected route.</p></div></header>
        <img src="/help/lineage/field-trace.png" alt="ActualAmount verified field lineage path" />
        <div className="lineage-help-legend">
          <span className="is-verified"><CheckCircleFilled /> Verified lineage</span>
          <span className="is-context"><EyeOutlined /> Broader resource context</span>
        </div>
        <p className="lineage-help-caption">Solid labels mark site observations, blue labels mark script-parsed QVD references, and dashed purple paths mark simulated upstream context.</p>
      </section>

      <Alert
        className="lineage-help-scope"
        type="info"
        showIcon
        message="Hybrid demo scope"
        description="Published App, referenced QVD, table, and field facts come from a read-only site scan. Database sources and missing 00/01 producer stages are explicitly simulated. Field highlights still come from the server lineage catalog; the browser never guesses links from matching names."
      />
    </div>
  </Drawer>
);
